import libphonenumber from 'google-libphonenumber';
import { Event, EventLog } from '../modules/apps/schedule/core/_models';
import { monitoringCaptureMessage } from './monitoring';
import {
  formatPhoneNumber as rpniFormatPhoneNumber,
  formatPhoneNumberIntl as rnpiFormatPhoneNumberIntl,
  parsePhoneNumber as rnpiParsePhoneNumber,
} from 'react-phone-number-input';
import { Provider } from '../modules/apps/providers/core/_models';
import { toAbsoluteUrl } from '../../_metronic/helpers';
import moment from 'moment';
import { ProviderService } from '../modules/apps/services/core/_models';
import { rrulestr } from 'rrule';

const phoneUtil = libphonenumber.PhoneNumberUtil.getInstance();

// Strip free text and keep only digits + optional leading '+'.
// Mirrors common/phone.py::normalize_phone_number on the backend so frontend
// and backend agree on what a stored phone looks like.
const cleanPhoneInput = (raw: string): string => {
  const s = raw.trim();
  const plus = s.startsWith('+') ? '+' : '';
  const digits = s.replace(/\D/g, '');
  return digits ? plus + digits : '';
};

const tryParsePhone = (phoneNumber: string) => {
  const cleaned = cleanPhoneInput(phoneNumber);
  if (!cleaned) return undefined;
  try {
    return phoneUtil.parse(cleaned);
  } catch {}
  try {
    return phoneUtil.parse(cleaned, 'US');
  } catch {
    return undefined;
  }
};

const validatePhoneNumber = (phoneNumber: string | undefined) => {
  if (!phoneNumber) return true;
  const parsed = tryParsePhone(phoneNumber);
  return !!parsed && phoneUtil.isPossibleNumber(parsed);
};

const normalizePhoneNumber = (phoneNumber: string | undefined) => {
  if (!phoneNumber) return phoneNumber;
  const cleaned = cleanPhoneInput(phoneNumber);
  if (!cleaned) return undefined;
  const parsed = tryParsePhone(cleaned);
  if (!parsed || !phoneUtil.isPossibleNumber(parsed)) return cleaned;
  return phoneUtil.format(parsed, libphonenumber.PhoneNumberFormat.E164);
};

const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

const isNumber = (x: any) => typeof x === 'number';

// Helper to cast numeric-like inputs to numbers, allowing decimals.
// Returns `null`/`undefined` when the input cannot be parsed as a valid number.
const numberOrNull = (x: any) => (isNaN(parseFloat(x)) ? null : parseFloat(x));

const numberOrUndefined = (x: any) => (isNaN(parseFloat(x)) ? undefined : parseFloat(x));

// function to find first valid variable even if it's 0 (workaround for javascript shenanigans)
function getValidNumber(...args: (number | undefined)[]) {
  for (var i = 0; i < args.length; i++) {
    if (isNumber(args[i])) return args[i];
  }
  return undefined;
}

// const formatPhoneNumber = (value: string | undefined) => {
//   let formattedNumber;
//   if (value === undefined) return '';
//   const { length } = value;
//   // Filter non numbers
//   const regex = () => value.replace(/[^+0-9\.]+/g, '');

//   // Set area code with parenthesis around it
//   const countryCode = () => `${regex().slice(0, 2)}`;

//   // Set area code with parenthesis around it
//   const areaCode = () => `${regex().slice(2, 5)}`;

//   // Set formatting for first six digits
//   const firstThree = () => `${regex().slice(5, 8)}`;
//   const lastFour = () => `${regex().slice(8, 12)}`;

//   return `${countryCode()} (${areaCode()}) ${firstThree()}-${lastFour()}`;
// };

const formatPhoneNumber = (phone?: string) => {
  let phoneNumber = phone ? rnpiParsePhoneNumber(phone) : undefined;

  return phoneNumber && phoneNumber.country === 'US'
    ? rpniFormatPhoneNumber(phone!)
    : (phone && rnpiFormatPhoneNumberIntl(phone)) ||
        (phone && rpniFormatPhoneNumber(phone)) ||
        (phone && rpniFormatPhoneNumber('+1' + phone)) ||
        phone ||
        '';
};

function calculateDistanceKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const earthRadius = 6371; // Radius of the Earth in kilometers

  // Convert latitude and longitude to radians
  const lat1Rad = (lat1 * Math.PI) / 180;
  const lon1Rad = (lon1 * Math.PI) / 180;
  const lat2Rad = (lat2 * Math.PI) / 180;
  const lon2Rad = (lon2 * Math.PI) / 180;

  // Calculate the differences between the latitudes and longitudes
  const latDiff = lat2Rad - lat1Rad;
  const lonDiff = lon2Rad - lon1Rad;

  // Calculate the distance using the Haversine formula
  const a = Math.sin(latDiff / 2) ** 2 + Math.cos(lat1Rad) * Math.cos(lat2Rad) * Math.sin(lonDiff / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = earthRadius * c;
  return distance;
}

function calculateDistanceMiles(lat1: number, lon1: number, lat2: number, lon2: number): number {
  return calculateDistanceKm(lat1, lon1, lat2, lon2) * 1.6;
}

const isRichTextEmpty = (text?: string) => {
  const strippedText = text
    ? text
        .replace(/<[^>]*>/g, '')
        .replace(/[\n\s]/g, '')
        .trim()
    : '';
  return strippedText.length === 0;
};

function stringsFancyJoin(inputList: string[]) {
  if (inputList.length === 0) {
    return '';
  }

  if (inputList.length === 1) {
    return inputList[0];
  }

  const lastItem = inputList.pop();
  return inputList.join(', ') + ' and ' + lastItem;
}

const capitalize = (s: string) => (s && s[0].toUpperCase() + s.slice(1).toLowerCase()) || '';

const titlelize = (s: string) => {
  return s
    .toLowerCase()
    .split(' ')
    .map(function (word) {
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
};

const titlelizeDashedString = (s: string) => {
  return s
    .toLowerCase()
    .split('-')
    .map(function (word) {
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
};

const titlelizeUnderscoredString = (s?: string) => {
  if (s === undefined) {
    return undefined;
  }
  return s
    .toLowerCase()
    .split('_')
    .map(function (word) {
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
};

const renderWithLineBreaks = (textWithLineBreaks: string, className?: string) => {
  const lines = textWithLineBreaks.split('\n');
  return lines.map((line: string, index: number) => (
    <p key={index} className={className}>
      {line}
    </p>
  ));
};

const getDaysAgo = (days: number): Date => {
  const today = new Date();
  today.setDate(today.getDate() - days);
  return today;
};

const isDateLessThanDaysOld = (dateToCheck: Date, days: number): boolean => {
  const twoWeeksAgo = getDaysAgo(days);
  return dateToCheck > twoWeeksAgo;
};

const fixToUTC = (d: Date) => {
  // rrule return string with 'Z' ending even though it's in local time :-(
  try {
    return new Date(d.toISOString().slice(0, -1));
  } catch (error) {
    // If the date is invalid, return the original date
    monitoringCaptureMessage(`Invalid date in fixToUTC: ${d} - ${error}`);
    return d;
  }
};

// Coerce any value to a valid Date or null. An *invalid* Date (getTime() === NaN)
// is still truthy, so it slips past react-datepicker's `date && format(date)`
// guard and makes date-fns format() throw "RangeError: Invalid time value",
// blanking the whole page. Use this before handing a date to react-datepicker.
const toValidDate = (value: unknown): Date | null => {
  if (value === null || value === undefined) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
};

const getEventTz = (event?: Event) => {
  if (!event) return undefined;
  const eventTz = event?.rrule?.match(/TZID=([^;:\n]+)/)?.[1];
  if (eventTz) {
    return eventTz;
  }
  return undefined;
};

function objectsHaveSameContent<T extends object>(obj1: T, obj2: T): boolean {
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  function compareUndefinedNull(var1: any, var2: any) {
    if ((var1 === undefined && var2 === null) || (var1 === null && var2 === undefined)) {
      return true;
    } else {
      return false;
    }
  }

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    if (
      obj1[key as keyof T] !== obj2[key as keyof T] &&
      !compareUndefinedNull(obj1[key as keyof T], obj2[key as keyof T])
    ) {
      return false;
    }
  }

  return true;
}

function lightenColor(color: string, factor: number): string {
  // Ensure the input is a valid hex color
  if (!/^#[0-9A-Fa-f]{6}$/.test(color)) {
    throw new Error('Invalid hex color format');
  }

  // Parse the hex color
  let r = parseInt(color.substring(1, 3), 16);
  let g = parseInt(color.substring(3, 5), 16);
  let b = parseInt(color.substring(5, 7), 16);

  // Lighten each color component
  r = Math.min(255, Math.floor(r + (255 - r) * factor));
  g = Math.min(255, Math.floor(g + (255 - g) * factor));
  b = Math.min(255, Math.floor(b + (255 - b) * factor));

  // Convert back to hex and return the new color
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

function isBrightColor(backgroundColor: string): boolean {
  // Convert hex color to RGB
  const hexToRgb = (hex: string): [number, number, number] => {
    // Remove the hash at the start if it's there
    hex = hex.replace(/^#/, '');

    // If shorthand notation, expand it (e.g., '03F' -> '0033FF')
    if (hex.length === 3) {
      hex = hex
        .split('')
        .map((char) => char + char)
        .join('');
    }

    // Parse the r, g, b values directly from the hex string
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    return [r, g, b];
  };

  // Calculate the luminance of the color
  const luminance = (r: number, g: number, b: number): number => {
    const a = [r, g, b].map((value) => {
      value /= 255;
      return value <= 0.03928 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4);
    });
    return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
  };

  // Convert background color to RGB
  const [r, g, b] = hexToRgb(backgroundColor);

  // Get luminance
  const bgLuminance = luminance(r, g, b);

  // Determine if the color is bright
  return bgLuminance > 0.4;
}

function getContrastColor(backgroundColor: string, lighten?: boolean): string {
  return isBrightColor(backgroundColor) ? (lighten ? '#777' : 'black') : lighten ? '#ddd' : 'white';
}

const providerAndClinicName = (provider?: Provider) =>
  provider?.clinic?.name ? `${provider?.clinic?.name} (${provider?.name})` : provider?.name || provider?.dba;

// Regular expression to match UUID version 4 format
const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

// Function to check if all elements in the booking array are valid UUIDs
const areAllValidUUIDs = (booking: string[]): boolean => {
  return booking.every((item) => uuidRegex.test(item));
};

function addDayMinusOneSecond(date?: Date | null): Date | undefined {
  if (!date) return undefined;
  const result = new Date(date); // Create a copy of the original date
  result.setDate(result.getDate() + 1); // Add one day
  result.setSeconds(result.getSeconds() - 1); // Subtract one second
  return result;
}

const baseDomain = window.location.hostname.split('.').slice(-2).join('.').toLowerCase();
const nameFromDomain = baseDomain === 'wiztherapy.com' ? 'WizTherapy' : 'Wiz';
const logoForDomain =
  baseDomain === 'wiztherapy.com'
    ? toAbsoluteUrl('/media/logos/wiztherapy-logo-white.svg')
    : toAbsoluteUrl('/media/logos/wiz-logo-white.svg');

function validateEmail(email: string): string | undefined {
  if (email === undefined) return undefined;
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email) ? email : undefined;
}

function validateName(name?: string): string | undefined {
  if (name === undefined) return undefined;
  const parts = name.split(' ');
  if (parts.length < 2) return undefined;
  return parts.every((part) => part.length >= 2) ? name : undefined;
}

function validateFirstName(name?: string): string | undefined {
  if (name === undefined) return undefined;
  const first = name.trim().split(' ')[0];
  return first && first.length >= 2 ? name : undefined;
}

type GuestPaymentDecision =
  | { ok: true; name: string; email?: string; guardianName?: string; guardianEmail?: string }
  | { ok: false; error: string };

// Derive a profile name from any field-key convention used by the form builder:
// `<prefix>.name`, `<prefix>.full_name`, or `<prefix>.first_name` + `<prefix>.last_name`.
function deriveProfileName(fd: Record<string, any>, prefix: string): string | undefined {
  const single = fd[`${prefix}.name`] ?? fd[`${prefix}.full_name`];
  if (typeof single === 'string' && single.trim()) return single;
  const parts = [fd[`${prefix}.first_name`], fd[`${prefix}.last_name`]].filter(
    (p) => typeof p === 'string' && p.trim()
  );
  const joined = parts.join(' ').trim();
  return joined || undefined;
}

function resolveGuestPayment(fd: Record<string, any>): GuestPaymentDecision {
  const clientName = deriveProfileName(fd, 'client');
  const guardianNameRaw = deriveProfileName(fd, 'guardian');
  const clientEmail = validateEmail(fd['client.email']);
  const guardianEmail = validateEmail(fd['guardian.email']);
  const guardianMode = !!(guardianNameRaw && guardianNameRaw.trim());

  if (guardianMode) {
    const firstName = validateFirstName(clientName);
    const guardianName = validateName(guardianNameRaw);
    const contactEmail = clientEmail || guardianEmail;
    if (firstName && guardianName && contactEmail) {
      return { ok: true, name: firstName, email: clientEmail, guardianName, guardianEmail };
    }
    if (!guardianName) return { ok: false, error: 'Guardian name must contain first and last name' };
    if (!firstName) return { ok: false, error: "Please provide the client's first name" };
    return { ok: false, error: 'Please provide a valid email' };
  }

  const name = validateName(clientName);
  if (clientEmail && name) return { ok: true, name, email: clientEmail };
  if (!clientEmail) return { ok: false, error: 'Invalid email' };
  return { ok: false, error: 'Name must contain first and last name' };
}

const scrollToElement = (elementId: string) => {
  const element = document.getElementById(elementId);
  if (element) {
    const headerHeight = document.querySelector('.kt_header')?.clientHeight || 0;
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.scrollY - headerHeight - 80;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });
  }
};

const getSafariVersion = () => {
  const ua = navigator.userAgent;
  const safariMatch = ua.match(/version\/(\d+(\.\d+)?)/i);
  if (safariMatch && safariMatch.length > 1) {
    return parseFloat(safariMatch[1]);
  }
  return null;
};

const safariVersion = getSafariVersion();

const isCancellationWindowExpired = (e: EventLog) => {
  const provider = e.event?.providers && e.event?.providers[0] && e.event?.providers[0];
  if (!provider) return false;
  
  const cancellationWindow = provider?.cancellation_window;
  if (!isNumber(cancellationWindow)) return false;

  const now = moment();
  const eventStart = moment(e.start);
  const hoursDifference = eventStart.diff(now, 'hours');
  return hoursDifference < cancellationWindow!;
};

const getProviderServiceParam = (providerService?: ProviderService, param?: string) => {
  if (!param) return undefined;
  
  return (providerService as any)?.[param] || 
         (providerService?.parent as any)?.[param] || 
         (providerService?.parent?.service as any)?.[param] || 
         (providerService?.service as any)?.[param];
};

const getProviderServiceName = (providerService?: ProviderService, provider?: Provider) => {
  return getProviderServiceParam(providerService, 'name');
};

const getRRwithExdates = (ev: Event) => {
  let rruleSetString = `${ev.rrule || ''}`;
  if (ev.exdate) rruleSetString += `\nEXDATE:${ev.exdate}`;
  if (ev.exrule) rruleSetString += `\nEXRULE:${ev.exrule}`;
  return rrulestr(rruleSetString, { forceset: true });
};

const isValidUrl = (url: string) => {
  return /^https?:\/\/[^\s$.?#].[^\s]*$/.test(url);
};

/**
 * WST-822 — derive the pre-discount figure for a bill from its net cost and
 * discount total. Returns null when there is nothing to show, so callers render
 * exactly as they did before this existed.
 */
export type DiscountDisplay = {
  gross: number;
  net: number;
  discount: number;
};

export const discountDisplay = (net?: number, discountTotal?: number): DiscountDisplay | null => {
  // Number(undefined) is NaN, which the isFinite guard below rejects. The API
  // can also hand these back as numeric strings, which Number() handles.
  const n = Number(net);
  const d = Number(discountTotal);
  if (!Number.isFinite(n) || !Number.isFinite(d) || d <= 0) {
    return null;
  }
  return {
    gross: Math.round((n + d) * 100) / 100,
    net: n,
    discount: d,
  };
};

export {
  validatePhoneNumber,
  normalizePhoneNumber,
  delay,
  isNumber,
  getValidNumber,
  formatPhoneNumber,
  numberOrNull,
  numberOrUndefined,
  calculateDistanceKm,
  calculateDistanceMiles,
  isRichTextEmpty,
  stringsFancyJoin,
  capitalize,
  titlelize,
  titlelizeDashedString,
  titlelizeUnderscoredString,
  isDateLessThanDaysOld,
  fixToUTC,
  toValidDate,
  objectsHaveSameContent,
  renderWithLineBreaks,
  getDaysAgo,
  lightenColor,
  getContrastColor,
  providerAndClinicName,
  isBrightColor,
  areAllValidUUIDs,
  addDayMinusOneSecond,
  baseDomain,
  nameFromDomain,
  logoForDomain,
  validateEmail,
  validateName,
  validateFirstName,
  resolveGuestPayment,
  scrollToElement,
  getEventTz,
  safariVersion,
  isCancellationWindowExpired,
  getProviderServiceParam,
  getProviderServiceName,
  getRRwithExdates,
  isValidUrl,
};
